import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Activity } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { getChartChrome } from '../../../theme/chartChrome';
import { concept2HeartRateZoneColor } from '../../../utils/concept2HeartRateZones';
import { formatElapsedSeconds } from '../../../utils/timeFormat';
import {
  CHART_TOOLTIP_CLASS,
  chartTooltipHtml as tt,
} from '../../coach/charts/common/chartTooltipClasses';
import {
  type Concept2WorkoutSeries,
  type Concept2WorkoutSeriesPoint,
  concept2SeriesHasMetric,
  formatConcept2PaceAxisLabel,
} from '../../../utils/concept2WorkoutSeries';
import Concept2CollapsibleSection from './Concept2CollapsibleSection';

interface Concept2WorkoutAnalysisProps {
  series: Concept2WorkoutSeries;
  /** Full panel with collapsible chrome for settings; bare chart for prototype review */
  variant?: 'panel' | 'chart';
}

type MetricKey = 'paceSeconds' | 'strokeRate' | 'heartRate';

interface MetricPanel {
  key: MetricKey;
  label: string;
  color: string;
  fill: boolean;
  invertY: boolean;
  formatValue: (value: number) => string;
}

interface PanelLayout {
  panel: MetricPanel;
  panelTop: number;
  yScale: d3.ScaleLinear<number, number>;
  plotHeight: number;
}

const METRIC_PANELS: MetricPanel[] = [
  {
    key: 'paceSeconds',
    label: 'Pace',
    color: '#1e3a8a',
    fill: true,
    invertY: true,
    formatValue: formatConcept2PaceAxisLabel,
  },
  {
    key: 'strokeRate',
    label: 'Stroke rate (spm)',
    color: '#64748b',
    fill: false,
    invertY: false,
    formatValue: (value) => String(Math.round(value)),
  },
  {
    key: 'heartRate',
    label: 'Heart rate (bpm)',
    color: '#dc2626',
    fill: false,
    invertY: false,
    formatValue: (value) => String(Math.round(value)),
  },
];

const LAYOUT_BY_VARIANT = {
  panel: {
    minPanelHeight: 108,
    panelGap: 14,
    minTotalHeight: 320,
  },
  chart: {
    minPanelHeight: 168,
    panelGap: 28,
    minTotalHeight: 620,
  },
} as const;

function finiteValues(points: Concept2WorkoutSeriesPoint[], key: MetricKey): number[] {
  return points
    .map((point) => point[key])
    .filter((value): value is number => value != null && Number.isFinite(value));
}

function average(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function appendColoredHeartRateLine(
  panelGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
  points: Concept2WorkoutSeriesPoint[],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  curve: d3.CurveFactory,
  minHeartRate: number,
  maxHeartRate: number
) {
  const segmentLine = d3
    .line<Concept2WorkoutSeriesPoint>()
    .defined((point) => point.heartRate != null && Number.isFinite(point.heartRate as number))
    .x((point) => xScale(point.timeSeconds))
    .y((point) => yScale(point.heartRate as number))
    .curve(curve);

  for (let index = 1; index < points.length; index += 1) {
    const start = points[index - 1];
    const end = points[index];
    if (start.heartRate == null || end.heartRate == null) {
      continue;
    }

    panelGroup
      .append('path')
      .datum([start, end])
      .attr('fill', 'none')
      .attr('stroke', concept2HeartRateZoneColor(end.heartRate, minHeartRate, maxHeartRate))
      .attr('stroke-width', 2.5)
      .attr('stroke-linecap', 'round')
      .attr('pointer-events', 'none')
      .attr('d', segmentLine);
  }
}

const Concept2WorkoutAnalysis: React.FC<Concept2WorkoutAnalysisProps> = ({
  series,
  variant = 'panel',
}) => {
  const [open, setOpen] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const { resolved: resolvedTheme } = useTheme();
  const layout = LAYOUT_BY_VARIANT[variant];

  const activePanels = METRIC_PANELS.filter((panel) =>
    concept2SeriesHasMetric(series.points, panel.key)
  );

  useEffect(() => {
    const container = containerRef.current;
    const svgElement = svgRef.current;
    const tooltipElement = tooltipRef.current;
    if (!container || !svgElement || !tooltipElement || activePanels.length === 0) {
      return;
    }

    const render = () => {
      const width = container.clientWidth;
      const height = Math.max(
        layout.minTotalHeight,
        layout.minPanelHeight * activePanels.length +
          layout.panelGap * (activePanels.length - 1) +
          48
      );
      const margin = { top: 12, right: 56, bottom: 32, left: 12 };
      const plotWidth = Math.max(120, width - margin.left - margin.right);
      const plotHeight =
        (height - margin.top - margin.bottom - layout.panelGap * (activePanels.length - 1)) /
        activePanels.length;
      const chrome = getChartChrome();
      const curve =
        series.source === 'strokes' ? d3.curveCatmullRom.alpha(0.35) : d3.curveStepAfter;
      const xScale = d3
        .scaleLinear()
        .domain([0, Math.max(series.totalSeconds, 1)])
        .range([0, plotWidth]);
      const heartRates = finiteValues(series.points, 'heartRate');
      const minHeartRate = heartRates.length > 0 ? Math.min(...heartRates) : 0;
      const maxHeartRate = heartRates.length > 0 ? Math.max(...heartRates) : 0;

      const svg = d3.select(svgElement);
      svg.selectAll('*').remove();

      const defs = svg.append('defs');
      const paceGradient = defs
        .append('linearGradient')
        .attr('id', 'concept2-pace-gradient')
        .attr('x1', '0')
        .attr('x2', '0')
        .attr('y1', '0')
        .attr('y2', '1');
      paceGradient
        .append('stop')
        .attr('offset', '0%')
        .attr('stop-color', '#1e3a8a')
        .attr('stop-opacity', 0.35);
      paceGradient
        .append('stop')
        .attr('offset', '100%')
        .attr('stop-color', '#1e3a8a')
        .attr('stop-opacity', 0.02);

      svg
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('width', width)
        .attr('height', height)
        .attr('role', 'img')
        .attr('aria-label', 'Concept2 workout analysis charts');

      const chartRoot = svg.append('g').attr('class', 'concept2-chart-root');
      const panelLayouts: PanelLayout[] = [];

      activePanels.forEach((panel, panelIndex) => {
        const values = finiteValues(series.points, panel.key);
        if (values.length === 0) {
          return;
        }

        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.12, panel.key === 'paceSeconds' ? 2 : 1);
        const yTop = panel.invertY ? min - padding : max + padding;
        const yBottom = panel.invertY ? max + padding : min - padding;
        const panelTop = margin.top + panelIndex * (plotHeight + layout.panelGap);
        const yScale = d3.scaleLinear().domain([yBottom, yTop]).range([plotHeight, 0]);
        const panelGroup = chartRoot
          .append('g')
          .attr('transform', `translate(${margin.left},${panelTop})`)
          .attr('pointer-events', 'none');

        panelLayouts.push({ panel, panelTop, yScale, plotHeight });

        panelGroup
          .append('g')
          .attr('class', 'grid')
          .call(
            d3
              .axisLeft(yScale)
              .ticks(3)
              .tickSize(-plotWidth)
              .tickFormat(() => '')
          )
          .call((group) => group.select('.domain').remove())
          .call((group) =>
            group.selectAll('line').attr('stroke', chrome.grid).attr('stroke-opacity', 0.8)
          );

        const line = d3
          .line<Concept2WorkoutSeriesPoint>()
          .defined(
            (point) => point[panel.key] != null && Number.isFinite(point[panel.key] as number)
          )
          .x((point) => xScale(point.timeSeconds))
          .y((point) => yScale(point[panel.key] as number))
          .curve(curve);

        if (panel.fill) {
          const area = d3
            .area<Concept2WorkoutSeriesPoint>()
            .defined(
              (point) => point[panel.key] != null && Number.isFinite(point[panel.key] as number)
            )
            .x((point) => xScale(point.timeSeconds))
            .y0(plotHeight)
            .y1((point) => yScale(point[panel.key] as number))
            .curve(curve);

          panelGroup
            .append('path')
            .datum(series.points)
            .attr(
              'fill',
              panel.key === 'paceSeconds' ? 'url(#concept2-pace-gradient)' : `${panel.color}22`
            )
            .attr('d', area);
        }

        if (panel.key === 'heartRate') {
          appendColoredHeartRateLine(
            panelGroup,
            series.points,
            xScale,
            yScale,
            curve,
            minHeartRate,
            maxHeartRate
          );
        } else {
          panelGroup
            .append('path')
            .datum(series.points)
            .attr('fill', 'none')
            .attr('stroke', panel.color)
            .attr('stroke-width', 2)
            .attr('d', line);
        }

        const panelAverage = average(values);
        if (panelAverage != null) {
          panelGroup
            .append('line')
            .attr('x1', 0)
            .attr('x2', plotWidth)
            .attr('y1', yScale(panelAverage))
            .attr('y2', yScale(panelAverage))
            .attr('stroke', panel.color)
            .attr('stroke-dasharray', '5 4')
            .attr('stroke-opacity', 0.75);
        }

        panelGroup
          .append('text')
          .attr('x', 0)
          .attr('y', -4)
          .attr('fill', chrome.axisLabel)
          .attr('font-size', 11)
          .attr('font-weight', 600)
          .text(
            panel.key === 'paceSeconds' ? `${panel.label} ${series.paceUnitLabel}` : panel.label
          );

        const yAxis = d3
          .axisRight(yScale)
          .ticks(3)
          .tickFormat((value) => panel.formatValue(Number(value)));
        panelGroup
          .append('g')
          .attr('transform', `translate(${plotWidth},0)`)
          .call(yAxis)
          .call((group) => group.select('.domain').remove())
          .call((group) =>
            group.selectAll('text').attr('fill', chrome.axisTick).attr('font-size', 10)
          )
          .call((group) =>
            group.selectAll('line').attr('stroke', chrome.axisLine).attr('stroke-opacity', 0.5)
          );
      });

      const xAxisGroup = svg
        .append('g')
        .attr('transform', `translate(${margin.left},${height - margin.bottom})`)
        .attr('pointer-events', 'none')
        .call(
          d3
            .axisBottom(xScale)
            .ticks(Math.max(4, Math.min(10, Math.floor(plotWidth / 72))))
            .tickFormat((value) => formatElapsedSeconds(Number(value)))
        );
      xAxisGroup.select('.domain').attr('stroke', chrome.axisLine);
      xAxisGroup.selectAll('text').attr('fill', chrome.axisTick).attr('font-size', 10);
      xAxisGroup.selectAll('line').attr('stroke', chrome.axisLine);

      const crosshair = svg
        .append('line')
        .attr('y1', margin.top)
        .attr('y2', height - margin.bottom)
        .attr('stroke', chrome.axisLine)
        .attr('stroke-dasharray', '4 4')
        .style('opacity', 0)
        .style('pointer-events', 'none');

      const markerLayer = svg.append('g').attr('class', 'concept2-scrub-markers');
      const markers = panelLayouts.map((layoutEntry) =>
        markerLayer
          .append('circle')
          .attr('r', 4)
          .attr('fill', layoutEntry.panel.color)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1.5)
          .style('opacity', 0)
          .style('pointer-events', 'none')
      );

      const interactionLayer = svg
        .append('rect')
        .attr('x', margin.left)
        .attr('y', margin.top)
        .attr('width', plotWidth)
        .attr('height', height - margin.top - margin.bottom)
        .attr('fill', 'transparent')
        .style('cursor', 'crosshair')
        .style('touch-action', 'none');

      const hideTooltip = () => {
        tooltipElement.style.opacity = '0';
        crosshair.style('opacity', 0);
        markers.forEach((marker) => marker.style('opacity', 0));
      };

      const showAtPointer = (event: PointerEvent) => {
        const [mouseX] = d3.pointer(event, svgElement);
        const plotX = mouseX - margin.left;
        if (plotX < 0 || plotX > plotWidth) {
          hideTooltip();
          return;
        }

        const targetTime = xScale.invert(plotX);
        const bisect = d3.bisector((point: Concept2WorkoutSeriesPoint) => point.timeSeconds).left;
        const index = Math.min(
          Math.max(bisect(series.points, targetTime) - 1, 0),
          series.points.length - 1
        );
        const point = series.points[index];
        const x = margin.left + xScale(point.timeSeconds);

        crosshair.attr('x1', x).attr('x2', x).style('opacity', 1);

        panelLayouts.forEach((layoutEntry, markerIndex) => {
          const value = point[layoutEntry.panel.key];
          const marker = markers[markerIndex];
          if (value == null || !Number.isFinite(value)) {
            marker.style('opacity', 0);
            return;
          }

          marker
            .attr('cx', x)
            .attr('cy', layoutEntry.panelTop + layoutEntry.yScale(value as number))
            .attr(
              'fill',
              layoutEntry.panel.key === 'heartRate'
                ? concept2HeartRateZoneColor(value as number, minHeartRate, maxHeartRate)
                : layoutEntry.panel.color
            )
            .style('opacity', 1);
        });

        const rows = activePanels
          .map((panel) => {
            const value = point[panel.key];
            if (value == null || !Number.isFinite(value)) {
              return null;
            }
            return `<div class="${tt.row}"><div class="${tt.rowInner}"><span class="${tt.label}">${panel.label}</span><span class="${tt.value}">${panel.formatValue(value)}</span></div></div>`;
          })
          .filter(Boolean)
          .join('');

        tooltipElement.innerHTML = `<div class="${tt.title}">${formatElapsedSeconds(point.timeSeconds)}</div>${rows}`;
        tooltipElement.style.opacity = '1';

        const tooltipWidth = tooltipElement.offsetWidth || 120;
        const tooltipHeight = tooltipElement.offsetHeight || 48;
        const left = Math.min(Math.max(x + 12, 8), container.clientWidth - tooltipWidth - 8);
        const top = Math.min(
          Math.max(margin.top + 8, 8),
          container.clientHeight - tooltipHeight - 8
        );
        tooltipElement.style.left = `${left}px`;
        tooltipElement.style.top = `${top}px`;
      };

      interactionLayer
        .on('pointerenter', (event) => showAtPointer(event))
        .on('pointermove', (event) => showAtPointer(event))
        .on('pointerdown', (event) => showAtPointer(event))
        .on('pointerleave', hideTooltip)
        .on('pointercancel', hideTooltip);
    };

    render();

    const observer = new ResizeObserver(() => render());
    observer.observe(container);
    return () => observer.disconnect();
  }, [activePanels, layout, resolvedTheme, series]);

  if (activePanels.length === 0) {
    return null;
  }

  const chartBody = (
    <div
      ref={containerRef}
      className={`relative w-full ${variant === 'chart' ? 'min-h-[70vh]' : ''}`}
    >
      <svg ref={svgRef} className="block w-full" />
      <div
        ref={tooltipRef}
        className={`${CHART_TOOLTIP_CLASS} pointer-events-none`}
        style={{ opacity: 0 }}
      />
      <p className="mt-3 text-xs text-content-muted">
        {series.source === 'strokes'
          ? 'Drag or hover across the charts to inspect per-stroke values. Heart rate color reflects workout intensity (green → yellow → red).'
          : 'Split-level trends from Concept2 Logbook. Re-sync to import stroke_data for continuous curves and scrubbing.'}
      </p>
    </div>
  );

  if (variant === 'chart') {
    return chartBody;
  }

  return (
    <Concept2CollapsibleSection
      title="Workout analysis"
      icon={<Activity className="h-4 w-4 text-rowing-blue" aria-hidden />}
      open={open}
      onToggle={() => setOpen((current) => !current)}
    >
      {chartBody}
    </Concept2CollapsibleSection>
  );
};

export default Concept2WorkoutAnalysis;
